import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Settings, 
  Save, 
  Upload, 
  Download,
  Database,
  Building2
} from "lucide-react";
import { API } from "@/lib/api";


export default function ConfigPage() {
  const [config, setConfig] = useState({
    company_name: "Clínica de Estética",
    default_tax_percent: 11.0,
    default_receipt_fee_percent: 13.0,
    default_loss_percent: 1.0,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await axios.get(`${API}/config`);
      setConfig(res.data);
    } catch (error) {
      console.error("Error fetching config:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/config`, config);
      toast.success("Configurações salvas com sucesso");
    } catch (error) {
      toast.error("Erro ao salvar configurações");
    } finally {
      setSaving(false);
    }
  };

  const handleExportBackup = () => {
    window.open(`${API}/export/backup`, '_blank');
    toast.success("Backup iniciado");
  };

  const handleImportBackup = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const data = JSON.parse(text);
      
      await axios.post(`${API}/import/backup`, data);
      toast.success("Backup importado com sucesso");
      
      // Refresh the page to load new data
      window.location.reload();
    } catch (error) {
      toast.error("Erro ao importar backup. Verifique o arquivo.");
    }
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-in" data-testid="config-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
          Configurações
        </h1>
        <p className="text-slate-500 mt-1">Gerencie as configurações do sistema</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Company Settings */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-teal-600" />
              <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>
                Dados da Empresa
              </CardTitle>
            </div>
            <CardDescription>
              Informações básicas da clínica
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="company_name">Nome da Empresa</Label>
              <Input
                id="company_name"
                value={config.company_name}
                onChange={(e) => setConfig({ ...config, company_name: e.target.value })}
                placeholder="Nome da sua clínica"
                data-testid="input-company-name"
              />
            </div>
          </CardContent>
        </Card>

        {/* Default Values */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-teal-600" />
              <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>
                Valores Padrão
              </CardTitle>
            </div>
            <CardDescription>
              Valores padrão para novas fichas de custo
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="default_tax">Impostos Padrão (%)</Label>
              <Input
                id="default_tax"
                type="number"
                step="0.1"
                value={config.default_tax_percent}
                onChange={(e) => setConfig({ ...config, default_tax_percent: parseFloat(e.target.value) || 0 })}
                className="font-mono"
                data-testid="input-default-tax"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="default_receipt">Taxa de Recebimento Padrão (%)</Label>
              <Input
                id="default_receipt"
                type="number"
                step="0.1"
                value={config.default_receipt_fee_percent}
                onChange={(e) => setConfig({ ...config, default_receipt_fee_percent: parseFloat(e.target.value) || 0 })}
                className="font-mono"
                data-testid="input-default-receipt"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="default_loss">Perda/Extravio Padrão (%)</Label>
              <Input
                id="default_loss"
                type="number"
                step="0.1"
                value={config.default_loss_percent}
                onChange={(e) => setConfig({ ...config, default_loss_percent: parseFloat(e.target.value) || 0 })}
                className="font-mono"
                data-testid="input-default-loss"
              />
            </div>

            <Button 
              onClick={handleSave} 
              className="w-full bg-teal-600 hover:bg-teal-700 mt-4"
              disabled={saving}
              data-testid="save-config-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Salvando..." : "Salvar Configurações"}
            </Button>
          </CardContent>
        </Card>

        {/* Backup & Restore */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Database className="w-5 h-5 text-teal-600" />
              <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>
                Backup e Restauração
              </CardTitle>
            </div>
            <CardDescription>
              Exporte ou importe os dados do sistema
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                variant="outline" 
                onClick={handleExportBackup}
                className="flex-1"
                data-testid="export-backup-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Exportar Backup (JSON)
              </Button>
              
              <div className="flex-1">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json"
                  onChange={handleImportBackup}
                  className="hidden"
                  id="backup-file"
                />
                <Button 
                  variant="outline" 
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full"
                  data-testid="import-backup-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Importar Backup (JSON)
                </Button>
              </div>
            </div>

            <div className="mt-4 p-4 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-600">
                <strong>Dica:</strong> Faça backups regulares para não perder seus dados. 
                O arquivo JSON contém todos os produtos, fichas de custo e configurações. Além disso, o sistema faz uma cópia automática por dia, que você baixa em Minha conta, na aba Meus dados.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
